import { useCallback, useEffect, useMemo, useState } from "react";
import { BaseControlledModal } from "../../../../../../common/components/base_controlled_modal";
import { DefaultDownloadInput } from "../../../../../../common/components/default_download_input";
import { NfeProduto, readNFe } from "../../../../../../common/utils/nfe_reader";
import { DefaultDatePicker } from "../../../../../../common/components/masked_inputs/default_date_picker_input";
import { DefaultAutocomplete } from "../../../../../../common/components/default_autocomplete";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { GridColDef } from "@mui/x-data-grid-pro";
import { FornecedorDropDown } from "../../../../../../common/domain/models/fornecedor_dropdown";
import { ProdutoServicoDropDown } from "../../../../../../common/domain/models/produto_servico_dropdown";
import { Autocomplete, Divider, IconButton, Paper, TextField } from "@mui/material";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";

import dayjs from "dayjs";
import DefaultInput from "../../../../../../common/components/default_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

/**
 * Alias
 */

type ImportXmlFunc = (
  idFornecedor: number | null,
  idCentroCusto: number | null,
  dataCadastro: Date | null,
  dataEmissao: Date | null,
  notaFiscal: string | null,
  produtos: {
    idProdutoServico: number | null,
    produtoNota: string | null,
    quantidade: number,
    valorUnitario: number,
    valorTotal: number,
  }[],
) => void;

/**
 * Produto
 */

interface Produto {
  codigo: string;
  produtoLabel: string | null;
  produto: number | null;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
}

/**
 * PaperWithNewButton
 */

const PaperWithNewButton = ({ onClick, children, ...props }: any) => {
  return (
    <Paper {...props}>
      <div>
        {children}
        <Divider />
        <div>
          <div
            onMouseDown={e => {
              e.preventDefault();
              onClick()
            }}
            className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold">
            <button type="button" className="text-sm text-primary-green">
              <DynamicIcon nameIcon="Add" size={20} className="text-primary-green" />
              <span>Novo</span>
            </button>
          </div>
        </div>
      </div>
    </Paper>
  );
}

/**
 * GenerateColumns
 */

type GenerateColumnsProps = {
  produtosServicosDropdown: ProdutoServicoDropDown[];
  onNewProduct: (codigo: string, produto: string) => void;
  onActionRemoveItem: (codigo: string) => void;
  onProdutoServicoChange: (codigo: string, idProdutoServico: number | null) => void;
};

function GenerateColumns({
  produtosServicosDropdown,
  onNewProduct,
  onActionRemoveItem,
  onProdutoServicoChange,
}: GenerateColumnsProps): GridColDef[] {
  return [
    {
      field: "codigo",
      filterable: false,
      headerName: "Codigo NF",
      width: 80,
      minWidth: 80,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center text-xs py-2">
          {value}
        </div>
      )
    },
    {
      field: "produtoLabel",
      filterable: false,
      headerName: "Produto Nota",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center text-xs py-2">
          {value}
        </div>
      )
    },
    {
      field: "produto",
      filterable: false,
      headerName: "Produto Sistema",
      flex: 2,
      minWidth: 150,
      sortable: false,
      valueGetter: (_, row) => ({ codigo: row['codigo'], produto: row['produto'] }),
      renderCell: ({ value: { codigo, produto } }) => {
        return (
          <div className="w-full h-full flex items-center text-xs py-2">
            <Autocomplete
              size="small"
              fullWidth={true}
              options={produtosServicosDropdown}
              getOptionKey={x => x.idProdutoServico}
              getOptionLabel={x => x.descricao}
              value={produto ? produtosServicosDropdown.find(x => x.idProdutoServico === produto) : null}
              onChange={(_, newValue) => {
                onProdutoServicoChange(codigo, newValue !== null ? newValue.idProdutoServico : null);
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    size="small"
                    label='Produtos'
                  />
                );
              }}
              slots={{
                paper: PaperWithNewButton,
              }}
              slotProps={{
                paper: {
                  onClick: () => onNewProduct(codigo, produto),
                },
                listbox: {
                  sx: {
                    '& .MuiAutocomplete-option': {
                      fontSize: '0.70rem',
                    },
                  },
                },
              }}
              sx={{
                '&.MuiAutocomplete-root': {
                  '.MuiFormControl-root': {
                    '.MuiFormLabel-root.MuiInputLabel-root': {
                      fontSize: '0.70rem',
                    },
                    '.MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink': {
                      fontSize: '0.90rem',
                    },
                    '.MuiInputBase-root.MuiOutlinedInput-root': {
                      '.MuiInputBase-input.MuiOutlinedInput-input': {
                        fontSize: '0.70rem',
                      },
                      '.MuiOutlinedInput-notchedOutline': {
                        'legend': {
                          'span': {
                            fontSize: '0.70rem',
                          },
                          fontSize: '0.70rem',
                        },
                        fontSize: '0.70rem',
                      },
                    },
                  },
                  padding: 0,
                },
              }}
            />
          </div>
        );
      },
    },
    {
      field: "quantidade",
      filterable: false,
      headerName: "Quantidade",
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center text-xs py-2">
          {value && (
            new Intl.NumberFormat('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value)
          )}
        </div>
      )
    },
    {
      field: "valorUnitario",
      filterable: false,
      headerName: "Valor Unitário",
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center text-xs py-2">
          {value && (
            new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }).format(value)
          )}
        </div>
      )
    },
    {
      field: "valorTotal",
      filterable: false,
      headerName: "Valor Total",
      width: 120,
      minWidth: 120,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center text-xs py-2">
          {value && (
            new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }).format(value)
          )}
        </div>
      )
    },
    {
      field: "acoes",
      filterable: false,
      headerName: "",
      width: 60,
      minWidth: 60,
      sortable: false,
      valueGetter: (_, row) => row['codigo'],
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center text-xs py-2">
          <IconButton onClick={() => onActionRemoveItem(value)}>
            <DynamicIcon nameIcon="DeleteForeverRounded" size={22} className="text-red-500" />
          </IconButton>
        </div>
      )
    },
  ];
}

/**
 * ImportXmlModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  centroCustoDropdown: CentroCustoDropDown[];
  fornecedoresDropdown: FornecedorDropDown[];
  produtosServicosDropdown: ProdutoServicoDropDown[];
  onClose: () => void;
  onNewProduct: (callback: (idProdutoServico: number) => void, produto: string) => void;
  onNewFornecedor: (callback: (idFornecedor: number) => void) => void;
  onImportXmlSubmit: ImportXmlFunc;
};

export const ImportXmlModal = ({
  open,
  saving,
  loading,
  centroCustoDropdown,
  fornecedoresDropdown,
  produtosServicosDropdown,
  onClose,
  onNewProduct,
  onNewFornecedor,
  onImportXmlSubmit,
}: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [fornecedor, setFornecedor] = useState<number | null>(null);
  const [centroCusto, setCentroCusto] = useState<number | null>(null);
  const [notaFiscal, setNotaFiscal] = useState<string | null>(null);
  const [dataCadastro, setDataCadastro] = useState<Date>(dayjs(new Date()).utc().toDate());
  const [dataEmissao, setDataEmissao] = useState<Date | null>(null);
  const [produtos, setProdutos] = useState<Produto[]>([]);

  const selectedFornecedor = useMemo(() => {
    if (fornecedor === null) return null;
    if (fornecedoresDropdown === null) return null;
    return fornecedoresDropdown.find(x => x.idFornecedor === fornecedor) ?? null;
  }, [
    fornecedor,
    fornecedoresDropdown,
  ]);

  const selectedCentroCusto = useMemo(() => {
    if (centroCusto === null) return null;
    if (centroCustoDropdown === null) return null;
    return centroCustoDropdown.find(x => x.id === centroCusto) ?? null;
  }, [
    centroCusto,
    centroCustoDropdown,
  ]);

  useEffect(() => {
    if (open) {
      setFile(null);
      setCentroCusto(null);
      setDataCadastro(dayjs(new Date()).utc().toDate());
    }
  }, [
    open,
  ]);

  const handleFileXmlLoader = useCallback(async (e: File | null) => {
    if (e) {
      const nfe = await readNFe(e);

      const nfeFornecedor = fornecedoresDropdown.find(x => x.razaoSocial.trim().toLowerCase() === nfe.fornecedor.trim().toLowerCase());
      setFornecedor(nfeFornecedor?.idFornecedor ?? null);

      setNotaFiscal(nfe.nf);
      setDataEmissao(dayjs(nfe.dataEmissao).utc().toDate());

      setProdutos(nfe.produtos.map(x => {
        const item = produtosServicosDropdown.find(y => {
          return y.descricao.toLowerCase().includes(x.produto.trim().toLowerCase());
        });

        return ({
          codigo: x.codigo,
          produtoLabel: x.produto,
          produto: item?.idProdutoServico ?? null,
          quantidade: x.quantidade,
          valorUnitario: x.valorUnitario,
          valorTotal: x.valorTotal,
        });
      }));
    }
    setFile(e);
  }, [
    fornecedoresDropdown,
    produtosServicosDropdown,
  ]);

  const handleFornecedorUpdate = useCallback(async (f: FornecedorDropDown | null) => {
    setFornecedor(f ? f.idFornecedor : null);
  }, [
  ]);

  const handleCentroCustoUpdate = useCallback(async (f: CentroCustoDropDown | null) => {
    setCentroCusto(f ? f.id : null);
  }, [
  ]);

  const handleProdutoEntryChange = useCallback((codigo: string, idProdutoServico: number | null) => {
    setProdutos(prev => {
      const produtoIndex = prev.findIndex(x => x.codigo === codigo);
      if (produtoIndex >= 0) {
        prev[produtoIndex].produto = idProdutoServico;
      }
      return prev;
    });
  }, [
  ]);

  const handleActionRemoveItem = useCallback((codigo: string) => {
    if (window.confirm('Você confirma a exclusão deste item?')) {
      setProdutos(prev => {
        return [...prev.filter(x => x.codigo !== codigo)];
      });
    }
  }, [
  ]);

  const handleNewProduct = useCallback((codigo: string, produto: string) => {
    onNewProduct(
      idProdutoServico => {
        setProdutos(prev => {
          const produtoIndex = prev.findIndex(x => x.codigo === codigo);
          if (produtoIndex >= 0) {
            prev[produtoIndex].produto = idProdutoServico;
          }
          return prev;
        });
      },
      produto,
    );
  }, [
    onNewProduct,
  ]);

  const handleNewFornecedor = useCallback(() => {
    onNewFornecedor(idFornecedor => setFornecedor(idFornecedor));
  }, [
    onNewFornecedor,
  ]);

  const columnsDef = useMemo(() => {
    return GenerateColumns({
      produtosServicosDropdown: produtosServicosDropdown,
      onNewProduct: handleNewProduct,
      onActionRemoveItem: handleActionRemoveItem,
      onProdutoServicoChange: handleProdutoEntryChange,
    });
  }, [
    produtosServicosDropdown,
    handleNewProduct,
    handleActionRemoveItem,
    handleProdutoEntryChange,
  ]);

  const handleSubmitImport = useCallback(() => {
    onImportXmlSubmit(
      fornecedor,
      centroCusto,
      dataCadastro,
      dataEmissao,
      notaFiscal,
      produtos.map(x => ({
        idProdutoServico: x.produto,
        produtoNota: x.produtoLabel,
        quantidade: x.quantidade,
        valorUnitario: x.valorUnitario,
        valorTotal: x.valorTotal,
      })),
    );
  }, [
    fornecedor,
    centroCusto,
    notaFiscal,
    dataCadastro,
    dataEmissao,
    produtos,
    onImportXmlSubmit,
  ]);

  return (
    <BaseControlledModal
      open={open}
      saving={saving}
      loading={loading}
      width="85rem"
      title="Importar Produtos de Arquivo XML"
      showConfirmButton={produtos.length > 0}
      confirmButtonLabel="Enviar"
      confirmSavingButtonLabel="Enviando..."
      onClose={onClose}
      onSubmit={handleSubmitImport}>
      {
        file === null &&
        <DefaultDownloadInput
          label='Selecione o arquivo XML'
          accept=".xml"
          saving={false}
          filename={null}
          value={file}
          onChange={e => handleFileXmlLoader(e)}
          onDeleteFile={() => { }}
        />
      }
      {
        file &&
        <>
          <div className="flex flex-col gap-y-4 w-full pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="w-full flex flex-row gap-2">
                <DefaultDatePicker
                  name='DataCadastro'
                  label='Data Cadastro'
                  required={true}
                  value={dayjs.utc(dataCadastro).local().toDate()}
                  readOnly={true}
                  onChangeValue={e => setDataCadastro(dayjs(e ?? new Date()).utc().toDate())}
                />

                <DefaultDatePicker
                  name='DataNotaFiscal'
                  label='Data Nota Fiscal'
                  required={true}
                  value={dayjs.utc(dataEmissao).local().toDate()}
                  readOnly={true}
                  onChangeValue={e => setDataEmissao(dayjs(e ?? new Date()).utc().toDate())}
                />
              </div>

              <DefaultAutocomplete
                label='Fornecedor'
                options={fornecedoresDropdown}
                getOptionKey={x => x.idFornecedor}
                getOptionLabel={x => x.nomeFantasia}
                value={selectedFornecedor}
                newButtonLabel="Novo fornecedor"
                onChange={e => handleFornecedorUpdate(e)}
                onNew={handleNewFornecedor}
              />

              <DefaultAutocomplete
                label='Centro Custo'
                options={centroCustoDropdown}
                getOptionLabel={x => x.title}
                getOptionKey={x => x.id}
                value={selectedCentroCusto}
                onChange={e => handleCentroCustoUpdate(e)}
              />

              <DefaultInput
                name='notaFiscal'
                label='Nota Fiscal'
                value={notaFiscal}
                onChangeValue={e => setNotaFiscal(e)}
              />
            </div>

            <StyledDataGrid
              tableName="entrada_produtos_lista_import_xml"
              disableColumnMenu={false}
              showToolbar={false}
              disableRowSelectionOnClick={true}
              getRowId={(row: NfeProduto) => row.codigo}
              getRowHeight={() => 'auto'}
              height="30rem"
              columns={columnsDef}
              loading={loading}
              checkboxSelection={false}
              rows={produtos}
            />

          </div>
        </>
      }
    </BaseControlledModal>
  );
}